import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import ModifiedTag from '@components/common/form/ModifiedField';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { selectOrganizationByCode } from '@redux/config/config.selectors';
import { useCreateOrganizationMutation, useUpdateOrganizationMutation } from '@services/config/organization';
import { haveSameUniqueElements } from '@utils/common.utils';
import { organizationSchemaValidation } from '@validations/organization.schema';
import { FormikProvider, useFormik } from 'formik';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { Organization } from '@/types/config/config.types';
import { DroneClearanceEnum } from '@/types/utm/drone.types';

import OrganizationAlertForm from './OrganizationAlertsForm';
import OrganizationGeneralForm from './OrganizationGeneralForm';
import OrganizationSettingsForm from './OrganizationSettingsForm';

type Props = {
  editedOrganizationCode: string | undefined;
  setEditedOrganizationCode: Dispatch<SetStateAction<string | null | undefined>>;
};

const emptyOrganization: Partial<Organization> = {
  id: undefined,
  name: '',
  email: '',
  platforms: [],
  organizationParameter: {
    defaultClearance: DroneClearanceEnum.FULL_CLEARANCE_WITHIN_FLIGHTPLAN,
    enableLadAlerts: true,
    enablePeriAlerts: true,
    enableTechAlerts: true,
    enableUtmAlerts: true,
    enableProcedureAutoArchive: true,
    expiredNotificationsTtl: 60,
    projectionTime: 60,
  },
};

type OrganizationDialogProps = {
  isNewOrganization: boolean;
  name: string | undefined;
} & Omit<CockpitDialogProps, 'children'>;

function OrganizationDialog({
  isNewOrganization,
  name,
  open,
  isLoading,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<OrganizationDialogProps>) {
  return (
    <CockpitDialog
      open={open}
      isLoading={isLoading}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage
          id={`components.organizations.popup.${isNewOrganization ? 'descriptionSaveNew' : 'descriptionSave'}`}
          values={{ name }}
        />
      </Text>
    </CockpitDialog>
  );
}

export default function OrganizationFormContainer({
  editedOrganizationCode,
  setEditedOrganizationCode,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const organization =
    useAppSelector((state) => selectOrganizationByCode(state, editedOrganizationCode ?? '')) ?? emptyOrganization;
  const activeSite = useAppSelector(selectActiveSite);
  const [createOrganization, { isLoading: isLoadingCreate }] = useCreateOrganizationMutation();
  const [updateOrganization, { isLoading: isLoadingUpdate }] = useUpdateOrganizationMutation();
  const isNewOrganization = editedOrganizationCode === undefined;
  const validationSchema = useValidation(organizationSchemaValidation);

  const formik = useFormik<Partial<Organization>>({
    initialValues: organization,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isNewOrganization) {
        createOrganization({ ...values, code: uuidv4(), site: activeSite?.code ?? 'local' } as Organization)
          .unwrap()
          .then((organization) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.organizations.message.add' }, { name: organization.name }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedOrganizationCode(null);
          });
      } else {
        updateOrganization(values as Organization)
          .unwrap()
          .then((organization) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.organizations.message.update' }, { name: organization.name }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedOrganizationCode(null);
          });
      }
    },
  });

  const hasGeneralTabChanged = () => {
    return (
      formik.initialValues.name !== formik.values.name ||
      !haveSameUniqueElements(formik.initialValues.platforms!, formik.values.platforms!) ||
      formik.initialValues.email !== formik.values.email
    );
  };

  const hasSettingsTabChanged = () => {
    return (
      formik.initialValues.organizationParameter?.enableProcedureAutoArchive !==
        formik.values.organizationParameter?.enableProcedureAutoArchive ||
      formik.initialValues.organizationParameter?.expiredNotificationsTtl !==
        formik.values.organizationParameter?.expiredNotificationsTtl ||
      formik.initialValues.organizationParameter?.defaultClearance !==
        formik.values.organizationParameter?.defaultClearance
    );
  };

  const hasAlertsTabChanged = () => {
    return (
      formik.initialValues.organizationParameter?.enableLadAlerts !==
        formik.values.organizationParameter?.enableLadAlerts ||
      formik.initialValues.organizationParameter?.enableUtmAlerts !==
        formik.values.organizationParameter?.enableUtmAlerts ||
      formik.initialValues.organizationParameter?.enableTechAlerts !==
        formik.values.organizationParameter?.enableTechAlerts ||
      formik.initialValues.organizationParameter?.enablePeriAlerts !==
        formik.values.organizationParameter?.enablePeriAlerts
    );
  };

  const hasChanged = () => {
    return hasGeneralTabChanged() || hasSettingsTabChanged() || hasAlertsTabChanged();
  };

  return (
    <CockpitEditorLayout
      dialog={
        <OrganizationDialog
          open={open}
          isLoading={isLoadingCreate || isLoadingUpdate}
          confirmLabel={formatMessage({ id: 'global.save' })}
          cancelLabel={formatMessage({ id: 'global.cancel' })}
          onConfirm={formik.submitForm}
          onCancel={() => setOpen(false)}
          isNewOrganization={isNewOrganization}
          name={formik.values.name}
        />
      }
      breadcrumbs={
        <Breadcrumb
          items={[
            {
              label: formatMessage({ id: 'components.organizations.title' }),
              action: () => setEditedOrganizationCode(null),
            },
            {
              label: isNewOrganization
                ? formatMessage({
                    id: 'components.organizations.newOrganization',
                  })
                : organization.name,
            },
          ]}
        />
      }
      onBack={() => setEditedOrganizationCode(null)}
      onSave={() => {
        if (!formik.isValid || !hasChanged()) {
          return;
        }
        if (organization.id === undefined) {
          void formik.submitForm();
        } else {
          setOpen(true);
        }
      }}
      isSaving={isLoadingCreate || isLoadingUpdate}
      isSaveDisabled={!formik.isValid || !hasChanged()}
    >
      <FormikProvider value={formik}>
        <Tabs.Root
          orientation="vertical"
          defaultValue="general"
          size="md"
          lazyMount
          variant="vertical"
          userSelect="none"
        >
          <Tabs.List>
            <Tabs.Trigger value="general">
              <HStack width="100%">
                <FormattedMessage id="components.organizations.general.title" />
                {hasGeneralTabChanged() && <ModifiedTag />}
              </HStack>
            </Tabs.Trigger>
            <Tabs.Trigger value="settings">
              <HStack width="100%">
                <FormattedMessage id="components.organizations.settings.title" />
                {hasSettingsTabChanged() && <ModifiedTag />}
              </HStack>
            </Tabs.Trigger>
            <Tabs.Trigger value="alerts">
              <HStack width="100%">
                <FormattedMessage id="components.organizations.alerts.title" />
                {hasAlertsTabChanged() && <ModifiedTag />}
              </HStack>
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.ContentGroup backgroundColor="neutral.700">
            <Tabs.Content value="general">
              <OrganizationGeneralForm />
            </Tabs.Content>
            <Tabs.Content value="settings">
              <OrganizationSettingsForm />
            </Tabs.Content>
            <Tabs.Content value="alerts">
              <OrganizationAlertForm />
            </Tabs.Content>
          </Tabs.ContentGroup>
        </Tabs.Root>
      </FormikProvider>
    </CockpitEditorLayout>
  );
}
